import React, { useEffect, useRef } from 'react';
import { Modal } from '../common';

interface FirstEntryCelebrationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Translations for modal content */
  translations: {
    title: string;
    subtitle: string;
    message: string;
    hint: string;
    cta: string;
    dayLabel: string;
  };
}

const CONFETTI_COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#ffd700'];
const CONFETTI_COUNT = 50;

/**
 * FirstEntryCelebrationModal - Celebrates user's first mood entry
 *
 * Ported from:
 * - app/views/diaries/shared/_first_entry_celebration.html.erb
 * - app/javascript/controllers/first_entry_celebration_controller.js
 */
export function FirstEntryCelebrationModal({
  isOpen,
  onClose,
  translations,
}: FirstEntryCelebrationModalProps) {
  const confettiContainerRef = useRef<HTMLDivElement>(null);

  // Create confetti particles when modal opens
  useEffect(() => {
    if (!isOpen || !confettiContainerRef.current) return;

    // Clear existing confetti
    confettiContainerRef.current.innerHTML = '';

    // Create new confetti particles
    for (let i = 0; i < CONFETTI_COUNT; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.backgroundColor = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      confetti.style.animationDelay = Math.random() * 2 + 's';
      confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
      confettiContainerRef.current.appendChild(confetti);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        closeOnBackdropClick={false}
        ariaLabel={translations.title}
        centered
      >
        <div className="border-0 shadow-lg overflow-hidden">
          <div
            className="celebration-header text-center text-white py-5"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            <div ref={confettiContainerRef} className="confetti-container" />
            <div className="celebration-icon mb-3">
              <i className="bi bi-trophy-fill display-1"></i>
            </div>
            <h2 className="fw-bold mb-2">{translations.title}</h2>
            <p className="mb-0 opacity-90">{translations.subtitle}</p>
          </div>

          <div className="modal-body text-center py-4">
            <p className="lead mb-4">{translations.message}</p>

            <div className="streak-preview d-inline-flex align-items-center gap-2 bg-light rounded-pill px-4 py-2 mb-4">
              <div className="streak-day completed">
                <i className="bi bi-check-circle-fill text-success"></i>
                <small className="d-block text-muted">
                  {translations.dayLabel} 1
                </small>
              </div>
              <div className="streak-connector"></div>
              <div className="streak-day pending">
                <i className="bi bi-circle text-muted"></i>
                <small className="d-block text-muted">
                  {translations.dayLabel} 2
                </small>
              </div>
              <div className="streak-connector"></div>
              <div className="streak-day pending">
                <i className="bi bi-fire text-warning opacity-50"></i>
                <small className="d-block text-muted">
                  {translations.dayLabel} 3
                </small>
              </div>
            </div>

            <p className="text-muted small">{translations.hint}</p>
          </div>

          <div className="modal-footer border-0 justify-content-center pb-4">
            <button
              type="button"
              className="btn-primary-gradient btn-lg px-5"
              onClick={onClose}
            >
              {translations.cta}
            </button>
          </div>
        </div>
      </Modal>

      {/* Styles */}
      <style>{`
        .celebration-header {
          position: relative;
        }
        .celebration-icon {
          animation: bounce 1s ease infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .streak-connector {
          width: 30px;
          height: 2px;
          background: #dee2e6;
        }
        .streak-day {
          text-align: center;
        }
        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
        }
        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          opacity: 0;
          animation: confetti-fall 3s ease-out forwards;
        }
        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(-100%) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(300px) rotate(720deg);
          }
        }
      `}</style>
    </>
  );
}
